import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList, CdkDragHandle, CdkDragPlaceholder, moveItemInArray } from '@angular/cdk/drag-drop';
import { BaseComponent } from '@app/core/bases';
import { EbayPhoto, EbayStock } from '@app/core/interfaces/ebay';
import { EbayService } from '@app/core/services';
import { BlockTextarea, InputBlockEmitter, BlockInput } from "@app/shared";
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { takeUntil } from 'rxjs';
import { ConfirmationService, PrimeIcons } from 'primeng/api';
import Viewer from 'viewerjs';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressBarModule } from 'primeng/progressbar';

@Component({
  selector: 'app-product-detail-distribution-ebay-stock',
  imports: [BlockTextarea, BlockInput, ProgressSpinnerModule, ButtonModule, TagModule, CdkDropList, CdkDrag, CdkDragHandle, CdkDragPlaceholder, ConfirmDialogModule, ProgressBarModule],
  providers: [ConfirmationService],
  templateUrl: './product-detail-distribution-ebay-stock.html'
})
export class ProductDetailDistributionEbayStock extends BaseComponent implements OnInit {
  @Input() ebayStock!: EbayStock;
  @Output() ebayStockChange: EventEmitter<EbayStock> = new EventEmitter<EbayStock>();

  @Output() isSendingData: EventEmitter<InputBlockEmitter> = new EventEmitter<InputBlockEmitter>();
  @Output() needStockReload: EventEmitter<boolean> = new EventEmitter<boolean>();

  ebayPhotos: EbayPhoto[] = [];

  viewerActiveIndex: number = 0;
  viewerInstance!: Viewer;

  isLoadingData: boolean = true;
  isUpdatingData: boolean = false;
  action: 'drop' | 'send' | 'background' | 'init' | 'debug' | null = null;

  constructor(
    private ebayService: EbayService,
    private confirmationService: ConfirmationService,
  ) {
    super();
  }

  ngOnInit(): void {
    if (!this.ebayStock) return;

    this.getEbayPhotos();
  }

  ebayStockPublish(): void {
    this.confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir publier ce stock sur Ebay?',
      header: 'Confirmer la publication',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: PrimeIcons.CHECK,
      acceptButtonProps: {
        iconPos: 'right'
      },
      rejectIcon: PrimeIcons.TIMES,
      rejectButtonProps: {
        text: true,
        severity: 'danger'
      },
      accept: () => {
        this.isUpdatingData = true;
        this.action = 'send';
        this.apiCall$?.unsubscribe();
        this.apiCall$ = this.ebayService.postEbayStockPublish(this.ebayStock.id as string | number)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            complete: () => {
              this.apiCall$?.unsubscribe();
              this.isUpdatingData = false;
              this.action = null;
              this.toastService.add({ severity: 'success', summary: 'Succès', detail: `Le stock Ebay a été publié` })
              this.needStockReload.emit(true)
              this.getEbayStock();
            }, error: (err: any) => {
              if(err.status === 400) {
                this.toastService.add({ severity: 'error', summary: 'Erreur', detail: `${err.error?.detail}`, life: 90000 })
              }
              this.apiCall$?.unsubscribe();
              this.isUpdatingData = false;
              this.action = null;
            }
          });
      }
    });
  }

  getEbayStock(): void {
    this.isLoadingData = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.ebayService.getEbayStock(this.ebayStock.id as string | number)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (ebayStock: EbayStock) => {
          this.ebayStock = { ...ebayStock }
          this.ebayStockChange.emit(this.ebayStock)
        },
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isLoadingData = false;
        },
        error: () => {
          this.apiCall$?.unsubscribe();
          this.isLoadingData = false;
        }
      });
  }

  postEbayStockDebug() {
      this.isUpdatingData = true;
      this.action = 'debug';
      this.apiCall$?.unsubscribe();
      this.apiCall$ = this.ebayService.postDebugEbayStock(this.ebayStock.id as string | number)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (data) => {
              this.toastService.add({ severity: 'success', summary: data.message as string, detail: `Stock ID ${data.stock_ebay_id}\n -Old SKU: ${data.old_sku}\n -New SKU: ${data.nv_sku}`, life: 90000 })
          },
          error: () => {
            this.apiCall$?.unsubscribe();
            this.isUpdatingData = false;
            this.action = null;
          },
          complete: () => {
            this.apiCall$?.unsubscribe();
            this.isUpdatingData = false;
            this.action = null;
          }
        });
  }

  getEbayPhotos(): void {
    this.isLoadingData = true;
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.ebayService.getEbayPhotoCollection({ stock_ebay_id: this.ebayStock.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (collection) => {
          this.ebayPhotos = collection.results;
        },
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isLoadingData = false;
        }
      });
  }

  reInitEbayPhoto(): void {
    this.isUpdatingData = true;
    this.action = 'init';
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.ebayService.postReInitEbayStockPhotos(this.ebayStock.id as string | number)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isUpdatingData = false;
          this.action = null;
          this.getEbayPhotos();
        }
      });
  }

  removeBackgroundFromEbayPhoto(): void {
    this.isUpdatingData = true;
    this.action = 'background';
    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.ebayService.postRemoveBackgroundEbayStock(this.ebayStock.id as string | number)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isUpdatingData = false;
          this.action = null;
          this.getEbayPhotos();
        }
      });
  }

  sendingDataHandler(event: InputBlockEmitter) {
    this.isSendingData.emit(event);
  }

  showViewer(event: Event) {
    if (this.viewerInstance) {
      this.viewerInstance.destroy();
    }

    const gallery = document.createElement('div');
    this.ebayPhotos?.forEach((photo: EbayPhoto, index: number, ebayPhotos: EbayPhoto[]) => {
      const img = new Image();
      img.src = this.apiUrl + (photo.url ?? photo.photo?.url);
      gallery.appendChild(img)
    })

    this.viewerInstance = new Viewer(gallery, {
      transition: false,
      ...this.viewerOptions,
      initialViewIndex: this.viewerActiveIndex
    });
    this.viewerInstance.show()
  }

  onDrop(event: CdkDragDrop<any[]>): void {
    this.isUpdatingData = true;
    this.action = 'drop';

    moveItemInArray(this.ebayPhotos, event.previousIndex, event.currentIndex);
    this.ebayPhotos.map((ebayPhoto: any, index: number) => {
      ebayPhoto.ordre = index
    })

    const ordres: number[] = this.ebayPhotos.map((ebayPhoto) => ebayPhoto.id) as number[];

    this.apiCall$?.unsubscribe();
    this.apiCall$ = this.ebayService
      .postEbayPhotoOrder(ordres)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        complete: () => {
          this.apiCall$?.unsubscribe();
          this.isUpdatingData = false;
          this.action = null;
          this.toastService.add({ severity: 'success', summary: 'Succès', detail: `L'ordre des photos a été modifié` })
        }
      });
  }

  deletePhoto(photo: EbayPhoto): void {
    this.confirmationService.confirm({
      message: 'Êtes-vous sûr de vouloir supprimer cette photo du stock Ebay?',
      header: 'Confirmer la suppression',
      icon: 'pi pi-exclamation-triangle',
      acceptIcon: PrimeIcons.CHECK,
      acceptButtonProps: {
        iconPos: 'right'
      },
      rejectIcon: PrimeIcons.TIMES,
      rejectButtonProps: {
        text: true,
        severity: 'danger'
      },
      accept: () => {
        this.ebayService
          .deleteEbayPhoto(photo.id as string | number)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              const photos = this.ebayPhotos?.filter((p: EbayPhoto) => p.id != photo.id) as EbayPhoto[] || []
              this.ebayPhotos = photos;
            },
            complete: () => this.toastService.add({ severity: 'success', summary: 'Succès', detail: 'Photo supprimé' }),
          });
      }
    });
  }
}
